import { React, useState, useEffect } from 'react';
import Select from "react-select";
import LeaseApiService from '../../services/apiService';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';

const LeaseDetails = ({ tableLoading, setTransectionData, transectionData, bankList, unitClass }) => {
    const apiService = new LeaseApiService();
    const { currencyPrecision } = useCurrency();
    // This tab is reused for both "Lease" and "PM" (Property Management) unit classes —
    // same underlying fields, relabeled per context (mirrors the legacy shared tpLeaseDetails tab).
    const isPM = unitClass === 'PM';
    const rentLabel = isPM ? 'Contract Value' : 'Rent Per Anum *';
    const rentPayableLabel = isPM ? 'Account Name' : 'Rent Payable To';
    const secDepositPayableLabel = isPM ? 'Bank Address' : 'Sec Deposit Payable To';
    const commissionPayableLabel = isPM ? 'SWIFT Code' : 'Commission Payable To';

    const decimals = currencyPrecision?.decimalNumbers || 2;
    const PERCENT_DECIMALS = 4;

    const handleAmountBlur = (e) => {
        const { name, value } = e.target;
        const num = value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
        setTransectionData((prev) => ({ ...prev, [name]: num.toFixed(decimals) }));
    };
    const formatDisplay = (value) => {
        if (value === '' || value === null || value === undefined || Number(value) === 0) {
            return (0).toFixed(decimals);
        }
        return value;
    };
    // Rent Per Annum <-> Rent/Month stay in sync: editing either one recalculates the other on blur.
    const handleRentPerAnumBlur = (e) => {
        const { value } = e.target;
        const num = value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
        const formatted = num.toFixed(decimals);
        setTransectionData((prev) => ({
            ...prev,
            rentPerAnum: formatted,
            ...(num > 0 ? { rentByMonth: (num / 12).toFixed(decimals) } : {}),
        }));
    };
    const handleRentByMonthBlur = (e) => {
        const { value } = e.target;
        const num = value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
        const formatted = num.toFixed(decimals);
        setTransectionData((prev) => ({
            ...prev,
            rentByMonth: formatted,
            ...(num > 0 ? { rentPerAnum: (num * 12).toFixed(decimals) } : {}),
        }));
    };
    // Display-only preview (not saved) — always freshly derived from Rent Per Annum.
    const monthlyRentPreview = (() => {
        const v = Number(transectionData?.rentPerAnum) || 0;
        return v > 0 ? (v / 12).toFixed(decimals) : '';
    })();
    // Security Deposit / Tenant Commission / Landlord Commission % all share Rent Per Annum as the base.
    const computePercentFromAmount = (amount, base) => {
        const b = Number(base) || 0;
        const a = Number(amount) || 0;
        if (!b) return '';
        const pct = (a / b) * 100;
        return Number.isFinite(pct) ? pct.toFixed(PERCENT_DECIMALS) : '';
    };
    const computeAmountFromPercent = (percent, base) => {
        const b = Number(base) || 0;
        const p = Number(percent) || 0;
        return ((b / 100) * p).toFixed(decimals);
    };
    const handleAmountBlurWithPercent = (amountField, percentField) => (e) => {
        const { value } = e.target;
        const num = value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
        const formattedAmount = num.toFixed(decimals);
        setTransectionData((prev) => {
            const pct = computePercentFromAmount(formattedAmount, prev?.rentPerAnum);
            return {
                ...prev,
                [amountField]: formattedAmount,
                ...(pct !== '' ? { [percentField]: pct } : {}),
            };
        });
    };
    const handlePercentBlur = (percentField) => (e) => {
        const { value } = e.target;
        const num = value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
        setTransectionData((prev) => ({ ...prev, [percentField]: num.toFixed(PERCENT_DECIMALS) }));
    };
    const handleCalculatePercentClick = (amountField, percentField) => () => {
        const base = transectionData?.rentPerAnum;
        const percentValue = transectionData?.[percentField];
        if (!base || percentValue === '' || percentValue === undefined || percentValue === null) return;
        const amount = computeAmountFromPercent(percentValue, base);
        setTransectionData((prev) => ({ ...prev, [amountField]: amount }));
    };

    const handleInputChange = (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;

        setTransectionData((prev) => {
            const updatedData = { ...prev, [name]: value };
            return updatedData;
        });
    };
    const handleDropDownChange = async ({ name, value }) => {
        setTransectionData(prev => ({ ...prev, [name]: value }));
    };
  
    const handleDateChange = async (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;
        setTransectionData((prev) => ({
            ...prev,
            [name]: value
        }));
    }
    return (
        <div className="cost-center-section my-3">
            <div className={`border-box2 p-3`}>
                {tableLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(255,255,255,0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                        }}
                    >
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                )}
                {/* <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px' }}> */}
                <div className="form-group">
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1">{rentLabel}</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.rentPerAnum)}
                            onChange={handleInputChange} onBlur={handleRentPerAnumBlur} name="rentPerAnum" /></div>
                        {monthlyRentPreview && <div className=" col-2"><label className="form-label pt-1">{monthlyRentPreview}</label></div>}
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1 start">Rent / Month *</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.rentByMonth)} onChange={handleInputChange} onBlur={handleRentByMonthBlur} name="rentByMonth" /></div>
                    </div>
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1 start">Rent per Day</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.rentPerDay)} onChange={handleInputChange} onBlur={handleAmountBlur} name="rentPerDay" /></div>
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1">Security Deposit</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.securityDeposit)} onChange={handleInputChange} onBlur={handleAmountBlurWithPercent('securityDeposit', 'securityDepositPerc')} name="securityDeposit" /></div>
                        <div className=" col-1"><h8 className='center p-1'>%</h8></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.securityDepositPerc} onChange={handleInputChange} onBlur={handlePercentBlur('securityDepositPerc')} name="securityDepositPerc" /></div>
                        <div className=" col-2"><a href="#" className="form-label pt-1" onClick={(e) => { e.preventDefault(); handleCalculatePercentClick('securityDeposit', 'securityDepositPerc')(); }}>Calculate %</a></div>
                    </div>
                    <div className="row ">
                        <div className=" col-3"><label className="form-label pt-1">Commission Paid By Tenant</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.tenantCommission)} onChange={handleInputChange} onBlur={handleAmountBlurWithPercent('tenantCommission', 'tenantCommissionPerc')} name="tenantCommission" /></div>
                        <div className=" col-1"><h8 className='center p-1'>%</h8></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.tenantCommissionPerc} onChange={handleInputChange} onBlur={handlePercentBlur('tenantCommissionPerc')} name="tenantCommissionPerc" /></div>
                        <div className=" col-2"><a href="#" className="form-label pt-1" onClick={(e) => { e.preventDefault(); handleCalculatePercentClick('tenantCommission', 'tenantCommissionPerc')(); }}>Calculate %</a></div>
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1">Commission Paid By Landlord</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.landlordCommission)} onChange={handleInputChange} onBlur={handleAmountBlurWithPercent('landlordCommission', 'landlordCommissionPerc')} name="landlordCommission" /></div>
                        <div className=" col-1"><h8 className='center p-1'>%</h8></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.landlordCommissionPerc} onChange={handleInputChange} onBlur={handlePercentBlur('landlordCommissionPerc')} name="landlordCommissionPerc" /></div>
                        <div className=" col-2"><a href="#" className="form-label pt-1" onClick={(e) => { e.preventDefault(); handleCalculatePercentClick('landlordCommission', 'landlordCommissionPerc')(); }}>Calculate %</a></div>
                    </div>
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1">Maintenance Fee</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.maintenanceFee)} onChange={handleInputChange} onBlur={handleAmountBlur} name="maintenanceFee" /></div>
                        <div className=" col-1"><h8 className='center p-1'>Due On</h8></div>
                        <div className='col-3'>
                            <DatePicker
                                todayButton="Today"
                                selected={transectionData?.dueDate}
                                onChange={(date) => handleDateChange({ name: 'dueDate', value: date })}
                                dateFormat="dd-MMM-yyyy"
                                className="p-2 form-control w-100"
                            />
                        </div>
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1 start">No of Installments *</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.installment} onChange={handleInputChange} name="installment" /></div>
                    </div>
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1">{rentPayableLabel}</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.rentPayable} onChange={handleInputChange} name="rentPayable" /></div>
                        <div className=" col-1"><h8 className='center p-1'>IBAN To</h8></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.IbanNo} onChange={handleInputChange} name="IbanNo" /></div>
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1">{secDepositPayableLabel}</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.secDepositPayable} onChange={handleInputChange} name="secDepositPayable" /></div>
                    </div>
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1">{commissionPayableLabel}</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.commissionPayable} onChange={handleInputChange} name="commissionPayable" /></div>
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1 start">Bank Name</label></div>
                        <div className=" col-3">
                            <Select
                                className="p-0 w-100"
                                theme={(theme) => ({
                                    ...theme,
                                    borderRadius: 5,
                                    padding: 9,
                                })}
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                        color: state.isFocused ? "#000 " : "#000",
                                        ":hover": {
                                            backgroundColor: "#F5F5F5",
                                            color: "#000 ",
                                        },
                                    }),
                                }}
                                value={transectionData?.bankName}
                                onChange={(selectedOption) => handleDropDownChange({ name: 'bankName', value: selectedOption })}
                                options={bankList?.map(country => ({ value: country, label: country?.text }))}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1">Account No</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={transectionData?.accountNo} onChange={handleInputChange} name="accountNo" /></div>
                    </div>
                    <div className="row my-2">
                        <div className=" col-3"><label className="form-label pt-1">Fixed Charges</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.fixedCharges)} onChange={handleInputChange} onBlur={handleAmountBlur} name="fixedCharges" /></div>
                    </div>
                    <div className="row">
                        <div className=" col-3"><label className="form-label pt-1">Variable Charges</label></div>
                        <div className=" col-3"><input type="text" className="p-2 form-control" value={formatDisplay(transectionData?.variableCharges)} onChange={handleInputChange} onBlur={handleAmountBlur} name="variableCharges" /></div>
                    </div>
                </div>
            </div>
        </div >
    )
}
export default LeaseDetails